import { useSyncExternalStore } from "react";
import { UNSAFE_createBrowserHistory as createBrowserHistory } from "react-router-dom";

// Split screen: the page (left) keeps the browser URL; the right pane is a MemoryRouter copy of the app.
// While split, a cross-section *record* open from the left (PO → its WO, dashboard row → customer…) is
// routed into the right pane instead; plain page switches (sidebar, "Go to") still change the left side.
export const mainHistory = createBrowserHistory({ window, v5Compat: true });

let snap = { on: false, initial: null, key: 0, paneLocation: null, ratio: "50" };
const subs = new Set();
const set = (patch) => { snap = { ...snap, ...patch }; subs.forEach((fn) => fn()); };
const subscribe = (fn) => { subs.add(fn); return () => subs.delete(fn); };
export const useSplit = () => useSyncExternalStore(subscribe, () => snap);

let paneNavigate = null;
export const registerPaneNavigate = (fn) => { paneNavigate = fn; return () => { if (paneNavigate === fn) paneNavigate = null; }; };
export const setPaneLocation = (loc) => {
  const cur = snap.paneLocation;
  if (cur?.pathname === loc.pathname && cur?.search === loc.search) return;
  set({ paneLocation: { pathname: loc.pathname, search: loc.search, state: loc.state } });
};
export const setSplitRatio = (ratio) => { if (ratio && ratio !== snap.ratio) set({ ratio }); };

const here = () => ({ pathname: mainHistory.location.pathname, search: mainHistory.location.search, state: mainHistory.location.state });
const fromPath = (p) => { const [pathname, qs] = String(p).split("?"); return { pathname, search: qs ? `?${qs}` : "" }; };

export const openSplit = (start = "same") => {
  const initial = !start || start === "same" ? here() : fromPath(start);
  set({ on: true, initial, key: snap.key + 1, paneLocation: initial });
};
export const closeSplit = () => set({ on: false, initial: null, paneLocation: null });
export const toggleSplit = (start) => (snap.on ? closeSplit() : openSplit(start));

const origPush = mainHistory.push;
const origReplace = mainHistory.replace;

export const swapPanes = () => {
  if (!snap.on || !paneNavigate || !snap.paneLocation) return;
  const left = here();
  const right = snap.paneLocation;
  paneNavigate({ pathname: left.pathname, search: left.search }, { replace: true, state: left.state });
  origReplace({ pathname: right.pathname, search: right.search }, right.state);
};

const section = (p) => (p || "").split("/")[1] || "";
const DETAIL_RE = /^\/(work-orders|clients|inspections)\/[^/]+/;
const QUIET_STATE_KEYS = new Set(["reset"]);
const isRecordOpen = (pathname, state) =>
  DETAIL_RE.test(pathname) || (!!state && typeof state === "object" && Object.keys(state).some((k) => !QUIET_STATE_KEYS.has(k)));

export const routesToPane = (to, state) => {
  const pathname = typeof to === "string" ? to.split("?")[0].split("#")[0] : to?.pathname || mainHistory.location.pathname;
  return snap.on && !!paneNavigate && section(pathname) !== section(mainHistory.location.pathname) && isRecordOpen(pathname, state);
};

mainHistory.push = (to, state, opts) => {
  if (routesToPane(to, state)) { paneNavigate(to, { state }); return; }
  origPush(to, state, opts);
};
